import { useParams } from "react-router-dom";
import TransactionList from "./components/TransactionList";
import AgreementServiceItemList from "./components/AgreementServiceItemList";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Stack,
  Typography,
} from "@mui/material";
import AccordingTemplate from "./components/AccordingTemplate";
import { useEffect, useState } from "react";
import { useNotifications } from "@toolpad/core/useNotifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import { IAppUser } from "src/interface/IUser";
import { getRoutes } from "src/utilities/routes";
import Description from "./components/Description";
import axios from "axios";
import { API_URL, handleSendMessage } from "src/utilities";
import { IRequestPayment } from "src/interface/IRequestPayment";
import SkeltonProgress from "src/components/SkeltonProgress";
import { IBaseAgreement } from "src/interface/IBaseAgreements";

const PayFactor = () => {
  const { customerId } = useParams() || "all";
  const [isAgreement, setIsAgreement] = useState(false);
  const [isAgreementPayed, setIsAgreementPayed] = useState(false);
  const notifications = useNotifications();
  const Routes = getRoutes();

  const [customer, setCustomer] = useState<IAppUser>();
  const [activeBaseAgreement, setActiveBaseAgreement] =
    useState<IBaseAgreement>();

  const { mutate: getCustomerByUserId, isPending } = useMutation({
    mutationFn: async (id: string) => {
      const response = await fetch(Routes.UsersRoutes + id, {
        method: "GET",
        headers: {
          Aceept: "application/json",
        },
      });
      return await response.json();
    },
    onSuccess: (responseData: IAppUser) => {
      if (responseData) {
        setCustomer(responseData);
      }
    },
    onError: (error) => {
      notifications.show("خطا در دریافت اطلاعات", {
        severity: "error",
        autoHideDuration: 3000,
      });
      console.log(error);
    },
  });

  // دریافت پایه قرارداد فعال
  const { mutate: getActiveBaseAgreement, isPending: isPendingBaseAgreement } =
    useMutation({
      mutationFn: async () => {
        const response = await fetch(Routes.BaseAgreementsActiveRoutes, {
          method: "GET",
          headers: {
            Aceept: "application/json",
          },
        });
        return await response.json();
      },
      onSuccess: (responseData: IBaseAgreement) => {
        if (responseData) {
          setActiveBaseAgreement(responseData);
        }
      },
      onError: (error) => {
        console.log("خطا در دریافت پایه قرارداد فعال:", error);
      },
    });

  useEffect(() => {
    if (customerId) {
      getCustomerByUserId(customerId);
    }
    getActiveBaseAgreement();
  }, [customerId]);

  const [amount, setAmount] = useState<number>(0);
  const [agreementAmount, setAgreementAmount] = useState<number>(0);
  const [agreementRef, setAgreementRef] = useState<number | undefined>(
    undefined
  );
  const [isAgreementChecked, setIsAgreementChecked] = useState(false);
  const handleCheckboxChange = (event: any) => {
    setIsAgreementChecked(event.target.checked);
  };
  const [loading, setLoading] = useState(false);
  const notify = useNotifications();

  const mutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(API_URL + "zarinpal/request", {
        amount: amount.toString(),
        agreementAmount: isAgreementPayed ? 0 : agreementAmount.toString(),
        agreementRef: isAgreementPayed ? undefined : agreementRef,
        description: "پرداخت صورتحساب",
        discountPrice: isAgreementPayed
          ? 0
          : activeBaseAgreement?.discount || 0,
        customerRef: customerId,
      });
      return response.data as IRequestPayment;
    },
    onSuccess: (reqPay) => {
      if (reqPay && reqPay.paymentUrl) {
        window.location.href = reqPay.paymentUrl;
      } else {
        handleSendMessage(notify, "لینک پرداخت دریافت نشد", "error");
      }
    },
    onError: (error: any) => {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message || "خطا در شروع پرداخت";
      handleSendMessage(notify, errorMessage, "error");
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const handlePayment = () => {
    setLoading(true);
    mutation.mutate();
  };

  return (
    <>
      <Typography textAlign={"center"} variant="h6">
        صورت حساب مشتری
      </Typography>
      <Stack
        direction={"row"}
        spacing={4}
        sx={{
          padding: 1,
          borderRadius: 2,
        }}
      >
        <Typography>
          نام:{" "}
          {isPending
            ? "درحال بارگیری اطلاعات ..."
            : customer?.fName + " " + customer?.lName}
        </Typography>
        <Typography>
          نام تجاری:{" "}
          {isPending ? "درحال بارگیری اطلاعات ..." : customer?.coName}
        </Typography>
      </Stack>
      {isPending ? (
        <SkeltonProgress />
      ) : (
        <>
          <TransactionList
            customerId={customerId ?? ""}
            setAmount={setAmount}
          />
          <AgreementServiceItemList
            setIsAgreementPayed={setIsAgreementPayed}
            setAgreementAmount={setAgreementAmount}
            customerId={customerId ?? ""}
            setIsAgreement={setIsAgreement}
            setAgreementRef={setAgreementRef}
          />
          {isAgreement && (
            <AccordingTemplate title="متن شرایط همکاری">
              <Description />
            </AccordingTemplate>
          )}
          {isAgreement && !isAgreementPayed && (
            <FormGroup sx={{ padding: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAgreementChecked}
                    onChange={handleCheckboxChange}
                  />
                }
                label="شرایط همکاری را مطالعه نموده و موافقم."
              />
            </FormGroup>
          )}
          <FormGroup sx={{ padding: 1 }}>
            <Button
              variant="contained"
              color="success"
              sx={{ height: 60 }}
              onClick={handlePayment}
              disabled={
                loading ||
                amount + agreementAmount <= 0 ||
                (amount === 0 && isAgreementPayed) ||
                (!isAgreementChecked && !isAgreementPayed && isAgreement)
              }
            >
              {loading ? "در حال انتقال به درگاه پرداخت..." : "تسویه"}
            </Button>
            {isAgreement && !isAgreementPayed && (
              <Typography
                sx={{ padding: 1 }}
                textAlign={"justify"}
                fontSize={14}
              >
                بخشی‌از مبلغ‌قرارداد به عنوان تخفیف (
                {activeBaseAgreement?.discount || 0}%)،در لحظه‌پرداخت به‌صورت
                خودکار و به‌نسبت زمان‌پرداخت محاسبه و لحاظ‌میگردد، لذا برای
                بهره‌مندی از تخفیف بیشتر، هرچه سریعتر نسبت‌به تسویه‌حساب
                اقدام‌فرمائید.
              </Typography>
            )}
          </FormGroup>
        </>
      )}
      {/* {isAgreement && (
        <FormGroup sx={{ padding: 1 }}>
          <Button variant="contained">پرداخت قسط</Button>
        </FormGroup>
      )} */}
    </>
  );
};

export default PayFactor;
